import type { ShipmentLineItem } from "~/shared/schemas/shipment-management-schema.ts";
import type { HandlingUnit } from "~/shared/types/quote-details-types";

const toStringOrNull = (value: number | null): string | null =>
	value == null ? null : String(value);

const codeOrName = (
	part: { name: string; code: string } | null,
): string | null => {
	const value = String(part?.code ?? part?.name ?? "").trim();
	return value === "" ? null : value;
};

// Flattens a structured hazmat class/group down to a single string for the
// shipment line item, preferring the canonical code (then name). `displayCode`
// is a UI-only label (e.g. "N/A" for the "None" packing group) and must never
// be persisted, or an unchanged value would be rewritten on save.
const hazmatText = (
	part?: { name: string; code: string; displayCode?: string } | null,
): string | null => {
	if (!part) {
		return null;
	}
	const value = (part.code ?? part.name).trim();
	return value === "" ? null : value;
};

/**
 * Maps the editor's structured `HandlingUnit` back onto the editable fields of a
 * shipment line item. The shipment stores everything as strings and keeps unit
 * and class as a single code, so numbers are stringified and the structured
 * unit/class objects are flattened back to their code.
 */
export const mapHandlingUnitToLineItem = (
	handlingUnit: HandlingUnit,
): Partial<ShipmentLineItem> => ({
	handlingUnit: toStringOrNull(handlingUnit.quantity),
	unit: codeOrName(handlingUnit.unit),
	totalWeight: toStringOrNull(handlingUnit.weight),
	length: toStringOrNull(handlingUnit.length),
	width: toStringOrNull(handlingUnit.width),
	height: toStringOrNull(handlingUnit.height),
	pieceCount: toStringOrNull(handlingUnit.pieceCount),
	stackable: handlingUnit.isStackable,
	hazmat: handlingUnit.isHazmat,
	commodityDescription: handlingUnit.description,
	class: codeOrName(handlingUnit.class),
	nmfc: handlingUnit.nmfc,
	density: handlingUnit.density,
	estimatedClass: handlingUnit.estClass ? String(handlingUnit.estClass) : null,
	hazmatIdentificationNumber: handlingUnit.unNumber ?? null,
	hazmatProperShippingName: handlingUnit.properShippingName ?? null,
	hazmatHazardClass: hazmatText(handlingUnit.hazmatClass),
	hazmatPackingGroup: hazmatText(handlingUnit.packingGroupId),
});
